import React, { useState } from 'react';
import { Star, ThumbsUp, Filter } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { testimonials } from '../data/testimonialsData';

export default function TestimonialsNew() {
  const [activeFilter, setActiveFilter] = useState('Tous');
  const [showAll, setShowAll] = useState(false);

  const filters = ['Tous', 'Retour Affectif', 'Voyance', 'Travail', 'Protection', 'Chance'];

  const filteredTestimonials = activeFilter === 'Tous' 
    ? testimonials 
    : testimonials.filter(t => t.category === activeFilter);

  const displayedTestimonials = showAll ? filteredTestimonials : filteredTestimonials.slice(0, 20);

  // Calculate stats
  const totalReviews = testimonials.length;
  const avgRating = 4.9;
  const ratingDistribution = {
    5: Math.floor(totalReviews * 0.94),
    4: Math.floor(totalReviews * 0.04),
    3: Math.floor(totalReviews * 0.01),
    2: Math.floor(totalReviews * 0.005),
    1: Math.floor(totalReviews * 0.005)
  };

  return (
    <section id="temoignages" className="py-12 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="text-sm text-yellow-500 font-semibold">Avis</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Témoignages de nos clients satisfaits
          </h2>
          <a 
            href="https://www.google.com/search?q=maitre+dia+voyant+marseille" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 transition-colors"
          >
            <span className="text-sm">Voir sur Google</span>
          </a>
        </div>

        {/* Rating Overview */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left: Overall Rating */}
              <div className="text-center md:text-left">
                <div className="inline-block mb-4">
                  <div className="text-6xl font-bold text-yellow-500 mb-2">{avgRating}</div>
                  <div className="flex gap-1 justify-center md:justify-start mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-yellow-500 text-yellow-500" />
                    ))}
                  </div>
                  <div className="text-gray-400 text-sm">{totalReviews} avis</div>
                </div>
                <div className="mt-4">
                  <p className="text-white font-semibold mb-1">Maître Diamma•Voyant Médium<span className="seo-hidden"> à Marseille</span></p>
                </div>
              </div>

              {/* Right: Distribution */}
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((stars) => (
                  <div key={stars} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-4">{stars}</span>
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full transition-all"
                        style={{ width: `${(ratingDistribution[stars] / totalReviews) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-400 w-12 text-right">
                      {ratingDistribution[stars]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          <Filter className="h-5 w-5 text-gray-400" />
          {filters.map((filter) => (
            <Button
              key={filter}
              variant={activeFilter === filter ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(filter)}
              className={activeFilter === filter 
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black' 
                : 'border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10'
              }
            >
              {filter}
            </Button>
          ))}
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {displayedTestimonials.map((testimonial) => (
            <Card 
              key={testimonial.id}
              className="p-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 hover:border-yellow-500/40 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                    <span className="text-lg font-bold text-yellow-500">{testimonial.initial}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-xs text-gray-400">{testimonial.location}•Local Guide</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-3">{testimonial.date}</p>
              
              <div className="flex gap-1 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                {testimonial.text}
              </p>

              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <span className="text-xs px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-yellow-500">
                  {testimonial.category}
                </span>
                <div className="flex items-center gap-1 text-gray-400">
                  <ThumbsUp className="h-4 w-4" />
                  <span className="text-xs">{testimonial.likes}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Show More Button */}
        {!showAll && filteredTestimonials.length > 20 && (
          <div className="text-center">
            <Button 
              onClick={() => setShowAll(true)}
              size="lg"
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold px-8 py-6 text-lg rounded-full shadow-lg shadow-yellow-500/30"
            >
              <Star className="h-5 w-5 mr-2" />
              Voir {filteredTestimonials.length - 20} avis de plus
            </Button>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center">
          <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Partagez votre expérience</h3>
            <p className="text-gray-400 mb-6">
              Vous avez consulté Maître Diamma ? Votre avis compte pour nous !
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                <a href="#contact">Consulter maintenant</a>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg"
                className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
              >
                <a 
                  href="https://www.google.com/search?q=maitre+dia+voyant+marseille" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Laisser un avis Google
                </a>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
